import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { BcmNode } from '../../api/bcmApi';
import { applyDisplayRules, type DisplayRule } from '../../hooks/useBcmBoard';

interface Props {
  parentNodes: BcmNode[];
  childNodes: BcmNode[];
  displayRules: DisplayRule[];
  onDrillDown: (node: BcmNode) => void;
  onUpdatePosition: (nodeId: string, x: number, y: number, w: number, h: number) => void;
  onAddChildRequest: (parentBcEntryId: string, x: number, y: number) => void;
  canAddChildren?: boolean;
  onContextMenu?: (node: BcmNode, clientX: number, clientY: number) => void;
}

interface DragState {
  nodeId: string;
  startMouseX: number;
  startMouseY: number;
  originX: number;
  originY: number;
  hasMoved: boolean;
}

interface PanState {
  startX: number;
  startY: number;
  originX: number;
  originY: number;
}

const HEADER_HEIGHT = 32;
const DRAG_THRESHOLD = 3;
export const BCM_CARD_WIDTH = 220;
export const BCM_CARD_HEIGHT = 84;
const EXPORT_PADDING = 32;
const truncateLabel = (value: string, width: number) => {
  const limit = Math.max(12, Math.floor((width - 28) / 7));
  if (value.length <= limit) return value;
  return `${value.slice(0, Math.max(9, limit - 3))}...`;
};

export function computeBcmViewportOrigin(nodes: Array<Pick<BcmNode, 'x' | 'y'>>) {
  if (nodes.length === 0) return { x: 0, y: 0 };
  return nodes.reduce(
    (origin, node) => ({
      x: Math.min(origin.x, node.x),
      y: Math.min(origin.y, node.y),
    }),
    { x: nodes[0].x, y: nodes[0].y },
  );
}

export function computeBcmExportBounds(nodes: Array<Pick<BcmNode, 'x' | 'y'>>) {
  if (nodes.length === 0) {
    return { x: 0, y: 0, width: BCM_CARD_WIDTH + EXPORT_PADDING * 2, height: BCM_CARD_HEIGHT + EXPORT_PADDING * 2 };
  }
  const origin = computeBcmViewportOrigin(nodes);
  const maxX = Math.max(...nodes.map((node) => node.x + BCM_CARD_WIDTH));
  const maxY = Math.max(...nodes.map((node) => node.y + BCM_CARD_HEIGHT));
  return {
    x: origin.x,
    y: origin.y,
    width: maxX - origin.x + EXPORT_PADDING * 2,
    height: maxY - origin.y + EXPORT_PADDING * 2,
  };
}

async function downloadSvgAsPng(svg: SVGSVGElement, fileName: string) {
  const xml = new XMLSerializer().serializeToString(svg);
  const svgBlob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);
  try {
    const image = new Image();
    image.decoding = 'async';
    const loaded = new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = reject;
    });
    image.src = url;
    await loaded;

    const canvas = document.createElement('canvas');
    canvas.width = Number(svg.getAttribute('width')) || image.width;
    canvas.height = Number(svg.getAttribute('height')) || image.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context unavailable');
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0);

    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = fileName;
    link.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export interface BcmCanvasHandle {
  exportPng: (fileName?: string) => Promise<void>;
}

export const BcmCanvas = forwardRef<BcmCanvasHandle, Props>(({
  parentNodes,
  childNodes,
  displayRules,
  onDrillDown,
  onUpdatePosition,
  onAddChildRequest,
  canAddChildren = true,
  onContextMenu,
}, ref) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 32, y: 32 });
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [panState, setPanState] = useState<PanState | null>(null);
  const [localPositions, setLocalPositions] = useState<Record<string, { x: number; y: number }>>({});
  const visibleNodes = useMemo(() => [...parentNodes, ...childNodes], [parentNodes, childNodes]);
  const viewportOrigin = useMemo(() => computeBcmViewportOrigin(visibleNodes), [visibleNodes]);
  const exportBounds = useMemo(() => computeBcmExportBounds(visibleNodes), [visibleNodes]);

  useImperativeHandle(ref, () => ({
    async exportPng(fileName = 'bcm-map.png') {
      if (!svgRef.current) return;
      const clone = svgRef.current.cloneNode(true) as SVGSVGElement;
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      clone.setAttribute('width', String(Math.ceil(exportBounds.width)));
      clone.setAttribute('height', String(Math.ceil(exportBounds.height)));
      clone.setAttribute('viewBox', `0 0 ${Math.ceil(exportBounds.width)} ${Math.ceil(exportBounds.height)}`);
      clone.setAttribute('style', `background:#f8fafc;width:${Math.ceil(exportBounds.width)}px;height:${Math.ceil(exportBounds.height)}px;`);
      const content = clone.querySelector('[data-bcm-content]');
      if (content) {
        content.setAttribute('transform', `translate(${EXPORT_PADDING - exportBounds.x},${EXPORT_PADDING - exportBounds.y})`);
      }
      await downloadSvgAsPng(clone, fileName);
    },
  }), [exportBounds]);

  useEffect(() => {
    setLocalPositions({});
  }, [parentNodes, childNodes]);

  const startDrag = (event: React.MouseEvent, node: BcmNode) => {
    event.stopPropagation();
    const current = localPositions[node.id] ?? { x: node.x, y: node.y };
    setDragState({
      nodeId: node.id,
      startMouseX: event.clientX,
      startMouseY: event.clientY,
      originX: current.x,
      originY: current.y,
      hasMoved: false,
    });
  };

  const handleMouseDown = (event: React.MouseEvent<SVGSVGElement>) => {
    if (event.button !== 1 && !event.altKey) return;
    event.preventDefault();
    setPanState({
      startX: event.clientX,
      startY: event.clientY,
      originX: pan.x,
      originY: pan.y,
    });
  };

  const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    if (panState) {
      setPan({
        x: panState.originX + (event.clientX - panState.startX),
        y: panState.originY + (event.clientY - panState.startY),
      });
      return;
    }

    if (!dragState) return;
    const dx = (event.clientX - dragState.startMouseX) / zoom;
    const dy = (event.clientY - dragState.startMouseY) / zoom;
    const hasMoved = Math.hypot(event.clientX - dragState.startMouseX, event.clientY - dragState.startMouseY) > DRAG_THRESHOLD;
    if (!hasMoved && !dragState.hasMoved) return;
    if (hasMoved && !dragState.hasMoved) {
      setDragState((prev) => (prev ? { ...prev, hasMoved: true } : prev));
    }
    setLocalPositions((prev) => ({
      ...prev,
      [dragState.nodeId]: {
        x: dragState.originX + dx,
        y: dragState.originY + dy,
      },
    }));
  };

  const handleMouseUp = () => {
    setPanState(null);
    if (!dragState) return;
    const current = localPositions[dragState.nodeId];
    const node = visibleNodes.find((item) => item.id === dragState.nodeId);
    if (dragState.hasMoved && current && node) {
      void Promise.resolve(onUpdatePosition(node.id, current.x, current.y, BCM_CARD_WIDTH, BCM_CARD_HEIGHT)).catch(() => {});
    }
    setDragState(null);
  };

  const handleWheel = (event: React.WheelEvent<SVGSVGElement>) => {
    event.preventDefault();
    setZoom((prev) => Math.max(0.2, Math.min(4, prev * (event.deltaY < 0 ? 1.1 : 0.9))));
  };

  const canvasFill = '#f8fafc';
  const parentFill = '#ffffff';
  const parentHeaderFill = '#f1f5f9';
  const parentStroke = '#cbd5e1';
  const childFill = '#ffffff';
  const childStroke = '#dbe3ef';
  const textFill = '#0f172a';
  const mutedTextFill = '#64748b';
  const accentTextFill = '#2563eb';

  return (
    <svg
      ref={svgRef}
      style={{ width: '100%', height: '100%', display: 'block', background: canvasFill }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
    >
      <g data-bcm-content="true" transform={`translate(${pan.x},${pan.y}) scale(${zoom}) translate(${-viewportOrigin.x},${-viewportOrigin.y})`}>
        {parentNodes.map((node) => {
          const pos = localPositions[node.id] ?? { x: node.x, y: node.y };
          const effect = applyDisplayRules(node, displayRules);
          const hasChildren = (node.entry?._computed?.children_count ?? 0) > 0;
          return (
            <g
              key={node.id}
              transform={`translate(${pos.x},${pos.y})`}
              onClick={() => {
                if (hasChildren) onDrillDown(node);
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                onContextMenu?.(node, e.clientX, e.clientY);
              }}
            >
              <rect
                width={BCM_CARD_WIDTH}
                height={BCM_CARD_HEIGHT}
                rx={6}
                ry={6}
                fill={parentFill}
                opacity={(effect.opacity as number) ?? 1}
                stroke={parentStroke}
                strokeWidth={1}
                strokeDasharray={effect.borderStyle === 'dashed' ? '6 4' : undefined}
                onMouseDown={(event) => startDrag(event, node)}
                style={{ cursor: 'grab' }}
              />
              <rect width={BCM_CARD_WIDTH} height={HEADER_HEIGHT} rx={6} ry={6} fill={parentHeaderFill} />
              <rect y={HEADER_HEIGHT - 6} width={BCM_CARD_WIDTH} height={6} fill={parentHeaderFill} />
              <rect
                width={4}
                height={BCM_CARD_HEIGHT}
                rx={2}
                ry={2}
                fill={(effect.color as string) ?? '#2563eb'}
                opacity={0.8}
              />
              <text
                x={16}
                y={HEADER_HEIGHT / 2}
                textAnchor="start"
                dominantBaseline="middle"
                fill={textFill}
                fontSize={13}
                fontWeight={600}
              >
                {truncateLabel(node.entry?.name ?? node.bc_entry_id, BCM_CARD_WIDTH - 44)}
              </text>
              <text
                x={16}
                y={HEADER_HEIGHT + 22}
                fill={mutedTextFill}
                fontSize={11}
              >
                {(node.entry?._computed?.children_count ?? 0)} дочерних
              </text>
              {canAddChildren && (
                <text
                  x={BCM_CARD_WIDTH - 14}
                  y={HEADER_HEIGHT / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={accentTextFill}
                  fontSize={18}
                  onClick={(event) => {
                    event.stopPropagation();
                    onAddChildRequest(node.bc_entry_id, pos.x + 24, pos.y + HEADER_HEIGHT + 24);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  +
                </text>
              )}
            </g>
          );
        })}

        {childNodes.map((node) => {
          const pos = localPositions[node.id] ?? { x: node.x, y: node.y };
          const effect = applyDisplayRules(node, displayRules);
          const hasChildren = (node.entry?._computed?.children_count ?? 0) > 0;
          return (
            <g
              key={node.id}
              transform={`translate(${pos.x},${pos.y})`}
              onClick={() => {
                if (hasChildren) onDrillDown(node);
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                onContextMenu?.(node, e.clientX, e.clientY);
              }}
            >
              <rect
                width={BCM_CARD_WIDTH}
                height={BCM_CARD_HEIGHT}
                rx={5}
                ry={5}
                fill={childFill}
                opacity={(effect.opacity as number) ?? 1}
                stroke={childStroke}
                strokeWidth={1}
                strokeDasharray={effect.borderStyle === 'dashed' ? '4 3' : undefined}
                onMouseDown={(event) => startDrag(event, node)}
                style={{ cursor: hasChildren ? 'pointer' : 'grab' }}
              />
              <rect
                width={3}
                height={BCM_CARD_HEIGHT}
                rx={1.5}
                ry={1.5}
                fill={(effect.color as string) ?? (hasChildren ? '#2563eb' : '#94a3b8')}
              />
              <text
                x={14}
                y={24}
                textAnchor="start"
                fill={textFill}
                fontSize={12}
                fontWeight={500}
              >
                {truncateLabel(node.entry?.name ?? node.bc_entry_id, BCM_CARD_WIDTH)}
              </text>
              <text
                x={14}
                y={BCM_CARD_HEIGHT - 14}
                textAnchor="start"
                fill={mutedTextFill}
                fontSize={10}
              >
                {(node.entry?._computed?.children_count ?? 0)} доч.
                {node.entry?._computed?.has_linked_systems ? ' · связи' : ''}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
});
